import { useState, FC, useEffect } from 'react';
import {
  Box,
  Input,
  Text,
  Textarea,
  Flex,
  Portal,
  Dialog,
  Tabs,
  Checkbox,
  RadioGroup,
  Menu
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { CustomButton } from './UI/CustomButton';
import { MdAttachFile, MdCancel, MdExpandMore, MdFileUpload } from 'react-icons/md';
import { useToastContext } from './UI/ToastManager';
import { number } from 'framer-motion';
import { API_URL } from '../utils/graphqlRequest';
import { formatConstraintValue } from '../utils/constraintFormat';
import { uploadVideoMultipart } from '../query/useUploadVideoMutation';

interface ValueChangeDetails<T = string> {
  value: T;
}

interface AddWorkoutInfoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workoutInfo?: WorkoutInfoData | null; // null for add, object for edit
  mode?: 'add' | 'edit';
  constantsData: any;
}

type WorkoutInfoData = {
    id: string | number | null;
    name: string;
    displayName: Array<{
      language: string;
      text: string;
    }>,
    description: Array<{
      language: string;
      text: string;
    }>;
    lastUpdateDate: string;
    videos: Array<{
      video: {
        source?: string;
        title?: string;
        duration?: string;
      };
      language: string;
    }>;
    poster: {
      source: string;
    };
    selector: {
      focus: string[];
      sport: string[];
      flags: string[];
      timeMin: number | string;
      timeMax: number | string;
      distanceMin: number | string;
      distanceMax: number | string;
      dplusMin: number | string;
      dplusMax: number | string;
    };
    status: string;
  };

const languages = ['fr', 'en', 'it', 'de', 'nl', 'es'];

// Helper to convert hh:mm:ss to seconds
function durationToSeconds(duration: string): number | null {
  if (!duration) return null;
  const parts = duration.split(':').map(Number);
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  } else if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  } else if (parts.length === 1) {
    return parts[0];
  }
  return null;
}

// Helper to convert seconds to hh:mm:ss
function secondsToDuration(seconds: number | null): string {
  if (!seconds) return '';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

// Helper to remove empty fields from objects recursively
function removeEmptyFields(obj: any): any {
  if (obj === null || obj === undefined) {
    return undefined;
  }
  
  if (Array.isArray(obj)) {
    const filteredArray = obj
      .map(item => removeEmptyFields(item))
      .filter(item => item !== undefined && item !== null);
    return filteredArray.length > 0 ? filteredArray : undefined;
  }
  
  if (typeof obj === 'object') {
    const result: any = {};
    let hasNonEmptyFields = false;
    
    for (const [key, value] of Object.entries(obj)) {
      const cleanedValue = removeEmptyFields(value);
      if (cleanedValue !== undefined && cleanedValue !== null && cleanedValue !== '') {
        result[key] = cleanedValue;
        hasNonEmptyFields = true;
      }
    }
    
    return hasNonEmptyFields ? result : undefined;
  }
  
  // For primitive values, return as is (empty strings will be filtered out above)
  return obj;
}

const initialWorkoutInfo: WorkoutInfoData = {
  id: null,
  name: '',
  displayName: languages.map(lang => ({ language: lang, text: '' })),
  description: languages.map(lang => ({ language: lang, text: '' })),
  videos: languages.map(lang => ({ language: lang, video: {} })),
  selector: {
    focus: [],
    sport: [],
    flags: [],
    distanceMax: '',
    distanceMin: '',
    dplusMax: '',
    dplusMin: '',
    timeMax: '',
    timeMin: ''
  },
  status: 'draft',
  lastUpdateDate: '',
  poster: {
    source: ''
  }
};

export const AddWorkoutInfoDialog: FC<AddWorkoutInfoDialogProps> = ({ open, onOpenChange, workoutInfo, mode = 'add', constantsData }) => {
  const { showSuccess, showError } = useToastContext();
  const queryClient = useQueryClient();
  const [videoType, setVideoType] = useState<'upload' | 'url'>('upload');
  const [posterType, setPosterType] = useState<'upload' | 'url'>('url');
  const [langTab, setLangTab] = useState('en');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [workoutInfoData, setWorkoutInfoData] = useState<WorkoutInfoData>(initialWorkoutInfo);
  const [isUploading, setIsUploading] = useState(false);
  const [posterFile, setPosterFile] = useState<File | null>(null);
  const [isPublished, setIsPublished] = useState(false);
  // 1. Update workoutInfoData type and initialWorkoutInfoData to support 'source' in description
  const [translationVideoFiles, setTranslationVideoFiles] = useState<{ [lang: string]: File | null }>({});
  const [translationVideoTypes, setTranslationVideoTypes] = useState<{ [lang: string]: 'upload' | 'url' }>({});

  // Load workoutInfo data when editing
  useEffect(() => {
    if (workoutInfo && mode === 'edit') {
      const workoutInfoData = {
        ...workoutInfo,
        selector: {
          ...workoutInfo.selector,
          timeMax: secondsToDuration(Number(workoutInfo.selector.timeMax)),
          timeMin: secondsToDuration(Number(workoutInfo.selector.timeMin))
        },
        videos: workoutInfo.videos.map(item => ({
          ...item,
          video: {
            ...item.video,
            duration: typeof item.video.duration === 'number'
              ? secondsToDuration(item.video.duration)
              : item.video.duration
          }
        }))
      };
      setWorkoutInfoData(workoutInfoData);
      setPosterType(workoutInfo?.poster?.source ? 'url' : 'upload');
      
      // Set translation video types based on existing data
      const translationTypes: { [lang: string]: 'upload' | 'url' } = {};
      workoutInfo.videos.forEach(item => {
        if (item.video.source) {
          translationTypes[item.language] = 'url';
        } else {
          translationTypes[item.language] = 'upload';
        }
      });
      setTranslationVideoTypes(translationTypes);
      setIsPublished(workoutInfoData.status === 'published');
    } else {
      // Reset to initial state for add mode
      setWorkoutInfoData(initialWorkoutInfo);
      setVideoType('upload');
      setPosterType('url');
      setLangTab('en');
      setVideoFile(null);
      setTranslationVideoTypes({});
      setTranslationVideoFiles({});
      setIsPublished(false);
    }
  }, [workoutInfo, mode, open]);

  const updateWorkoutInfoField = (field: string, value: any) => {
    setWorkoutInfoData(prev => ({ ...prev, [field]: value }));
  };

  const selectsOptions = constantsData ? [
    { type: 'Focus', options: constantsData.workoutFocus?.map(formatConstraintValue) || [] },
    { type: 'Sport', options: constantsData.workoutSports?.map(formatConstraintValue) || [] },
    { type: 'Flags', options: constantsData.sessionFlags?.map(formatConstraintValue) || [] }
  ] : [];

  const updateNestedField = <K extends keyof WorkoutInfoData, F extends keyof WorkoutInfoData[K]>(
    section: K,
    field: F,
    value: any
  ) => {
    setWorkoutInfoData(prev => {
      const current = prev[section];
      // if (typeof current !== 'object' || current === null || Array.isArray(current)) return prev;

      return {
        ...prev,
        // @ts-ignore
        [section]: { ...current, [field]: value },
      };
    });
  };

  const findTextDataByLanguage = (section: 'description' | 'displayName', language: string) => {
    return workoutInfoData[section]?.find(item => item.language === language);
  };

  const findVideoDataByLanguage = (language: string) => {
    return workoutInfoData.videos.find(item => item.language === language);
  };

  const updateTextDataByLanguage = (section: 'description' | 'displayName', language: string, value: string) => {
    setWorkoutInfoData(prev => {
      const arr = Array.from(prev[section] || []);
      const index = arr.findIndex(item => item.language === language);
      if (index !== -1) {
        // Update existing language entry
        arr[index] = { ...arr[index], text: value };
      } else {
        // Add new language entry
        arr.push({ language, text: value });
      }
      return { ...prev, [section]: arr };
    });
  };

  const updateVideoDataByLanguage = (language: string, field: keyof WorkoutInfoData['videos'][0]['video'], value: any) => {
    setWorkoutInfoData(prev => {
      const arr = Array.from(prev.videos);
      const index = arr.findIndex(item => item.language === language);
      if (index !== -1) {
        // Update existing language entry
        arr[index] = { 
          ...arr[index], 
          video: { ...arr[index].video, [field]: value }
        };
      } else {
        // Add new language entry
        arr.push({ 
          language, 
          video: { [field]: value }
        });
      }
      return { ...prev, videos: arr };
    });
  };

  // 3. Add helpers for updating translation image fields
  const updateTranslationVideoType = (lang: string, type: 'upload' | 'url') => {
    setTranslationVideoTypes(prev => ({ ...prev, [lang]: type }));
  };
  const updateTranslationVideoFile = (lang: string, file: File | null) => {
    setTranslationVideoFiles(prev => ({ ...prev, [lang]: file }));
  };

  const handleDialogOpenChange = (open: boolean) => onOpenChange(open);
  const handlePosterTypeChange = (details: ValueChangeDetails<'upload' | 'url'>) => setPosterType(details.value);
  const handleLangTabChange = (details: ValueChangeDetails<string>) => setLangTab(details.value);

  // Upload media files and return their URLs
  const uploadMedia = async (file: File, type: 'video' | 'image'): Promise<string | null> => {
    // Create the GraphQL query for upload
    const mutation = type === 'video' ? `
      mutation uploadVideo($video: Upload!, $videoType: String!) {
        uploadVideo(video: $video, videoType: $videoType) {
          success
          message
        }
      }
    ` : `
      mutation uploadImage($image: Upload!, $imageType: String!) {
        uploadImage(image: $image, imageType: $imageType) {
          success
          message
        }
      }
    `;

    // Create the operations map
    const operations = {
      query: mutation,
      variables: {
        [type === 'video' ? 'video' : 'image']: null,
        [type === 'video' ? 'videoType' : 'imageType']: type === 'video' ? 'workout_info_video' : 'workout_info_poster'
      }
    };

    // Create the file map
    const map = {
      [type === 'video' ? '0' : '0']: [`variables.${type === 'video' ? 'video' : 'image'}`]
    };

    const formData = new FormData();
    formData.append('operations', JSON.stringify(operations));
    formData.append('map', JSON.stringify(map));
    formData.append('0', file);

    const token = localStorage.getItem('authToken');

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      
      if (result.data?.uploadVideo?.success || result.data?.uploadImage?.success) {
        // The response should contain the uploaded file URL
        const uploadResult = result.data?.uploadVideo || result.data?.uploadImage;
        return uploadResult?.message || uploadResult?.url || uploadResult?.publicUrl;
      } else {
        const errorMessage = result.data?.uploadVideo?.message || result.data?.uploadImage?.message || 'Upload failed';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Upload error:', error);
      showError('Upload Failed', `Failed to upload ${type}. Please try again.`);
      return null;
    }
  };

  // 4. Update handleSaveWorkoutInfo to upload translation images if needed
  const handleSaveWorkoutInfo = async () => {
    setIsUploading(true);
    try {
      // First, upload any media files if needed
      let posterUrl = workoutInfoData?.poster?.source;
      let videos: {
        language: string;
        video: {
          source?: string;
          title?: string;
          duration?: string;
        };
      }[];

      // Upload translated videos if it's a files to upload
      if (translationVideoFiles && Object.keys(translationVideoFiles).length > 0) {
        const updatedVideosRaw = await Promise.all(
          Object.entries(translationVideoFiles).map(async ([lang, file]) => {
            if (!file) {
              showError(`Missing video file for language: ${lang}`);
              return undefined;
            }

            const uploadResult = await uploadVideoMultipart(file, showError);

            if (!uploadResult) {
              showError(`Failed to upload video for language: ${lang}`);
              return undefined;
            }

            const { publicUrl, posterUrl } = uploadResult;
            const originalVideo = workoutInfoData.videos.find(
              v => v.language.toLowerCase() === lang.toLowerCase()
            );

            return {
              language: lang,
              video: {
                source: publicUrl,
                title: originalVideo?.video?.title,
                // posterSource: posterUrl,
                duration: originalVideo?.video?.duration || ''
              }
            };
          })
        );

        const updatedVideos = updatedVideosRaw.filter(
          (v): v is NonNullable<typeof v> => v !== undefined
        );

        const updatedLangs = new Set(updatedVideos.map(v => v.language.toLowerCase()));

        const preservedVideos = workoutInfoData.videos.filter(
          v => !updatedLangs.has(v.language.toLowerCase())
        );
        videos = [...updatedVideos, ...preservedVideos];
      } else {
        videos = workoutInfoData.videos;
      }
      // Upload poster if it's a file upload
      if (posterType === 'upload' && posterFile) {
        const uploadedPosterUrl = await uploadMedia(posterFile, 'image');
        if (!uploadedPosterUrl) {
          setIsUploading(false);
          return; // Stop if upload failed
        }
        posterUrl = uploadedPosterUrl;
      }

    const mutation = mode === 'add' ? `
      mutation createWorkoutInfo($workoutInfo: WorkoutInfoInput!) {
        createWorkoutInfo(workoutInfo: $workoutInfo) {
          id
        }
      }
    ` : `
      mutation updateWorkoutInfo($id: ID!, $workoutInfo: WorkoutInfoUpdateInput!) {
        updateWorkoutInfo(id: $id, workoutInfo: $workoutInfo) {
          id
        }
      }
    `;

    const input: any = {
      name: workoutInfoData.name,
      poster: {
        ...workoutInfoData.poster,
        source: posterUrl,
      },
      selector: {
        ...workoutInfoData.selector,
        timeMax: durationToSeconds(workoutInfoData.selector?.timeMax?.toString() || ''),
        timeMin: durationToSeconds(workoutInfoData.selector?.timeMax?.toString() || ''),
        distanceMax: Number(workoutInfoData.selector.distanceMax),
        distanceMin: Number(workoutInfoData.selector.distanceMin),
        dplusMax: Number(workoutInfoData.selector.dplusMax),
        dplusMin: Number(workoutInfoData.selector.dplusMin)
      },
      status: workoutInfoData.status,
      description: workoutInfoData.description
        .filter(d => d.text.trim())
        .map(d => ({ ...d, language: d.language.toLowerCase() })),
      displayName: workoutInfoData.displayName
        .filter(d => d.text.trim())
        .map(d => ({ ...d, language: d.language.toLowerCase() })),
      videos: (videos.length ? videos : workoutInfoData.videos)
        .filter(v => v.video && v.video.source)
        .map(v => ({
          ...v,
          language: v.language.toLowerCase(),
          video: {
            ...v.video,
            duration: durationToSeconds(v.video.duration || ''),
          },
        })),
    };

    // Remove empty fields from the input before sending
    const cleanedInput = removeEmptyFields(input);
      const variables = mode === 'add' ? { workoutInfo: cleanedInput } : { id: workoutInfoData.id, workoutInfo: cleanedInput };
      const token = localStorage.getItem('authToken');

      try {
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            query: mutation,
            variables,
          }),
        });
        const result = await response.json();
        
        if (result.data?.createWorkoutInfo?.id || result.data?.updateWorkoutInfo?.id) {
          const action = mode === 'add' ? 'created' : 'updated';
          showSuccess(`Workout Info ${action}`, `The workout info has been successfully ${action}.`);
          
          // Invalidate the workout info query cache to refresh the list
          queryClient.invalidateQueries({ queryKey: ['workoutInfo'] });
          
          onOpenChange(false);
        } else {
          const action = mode === 'add' ? 'creating' : 'updating';
          showError(`${action.charAt(0).toUpperCase() + action.slice(1)} Failed`, `Failed to ${action} workout info. Please check your input and try again.`);
        }
      } catch (err) {
        const action = mode === 'add' ? 'creating' : 'updating';
        showError('Network Error', `An error occurred while ${action} the workout info. Please check your connection and try again.`);
      }
    } catch (err) {
      console.error('Save workout info error:', err);
      showError('Error', 'An error occurred while saving the workout info. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const convertFormattedValueToOriginal = (formatted: string): string => {
    return formatted
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-');
  };

  const handleRemoveSelectValue = (type: 'focus' | 'sport' | 'flags', formattedValue: string) => {
    const originalValue = convertFormattedValueToOriginal(formattedValue);

    const currentValues = workoutInfoData.selector[type] || [];

    const updatedValues = currentValues.filter((value: string) => value !== originalValue);

    const updatedSelector = {
      ...workoutInfoData.selector,
      [type]: updatedValues,
    };

    setWorkoutInfoData({
      ...workoutInfoData,
      selector: updatedSelector,
    });
  };

  const handleAddSelectValue = (type: 'focus' | 'sport' | 'flags', formattedValue: string) => {
    const originalValue = convertFormattedValueToOriginal(formattedValue);

    const currentValues = workoutInfoData.selector[type] || [];
    if (!currentValues.includes(originalValue)) {
      const updatedSelector = {
        ...workoutInfoData.selector,
        [type]: [...currentValues, originalValue],
      };

      setWorkoutInfoData({
        ...workoutInfoData,
        selector: updatedSelector,
      });
    }
  };

  const handleChangeSelectorInputs = (key: 'timeMin' | 'timeMax' | 'distanceMin' | 'distanceMax' | 'dplusMin' | 'dplusMax', val: string) => {
    setWorkoutInfoData({
      ...workoutInfoData,
      selector: {
        ...workoutInfoData.selector,
        [key]: val
      }
    })
  }

  const handlePublished = (isPublished: boolean) => {
    setIsPublished(isPublished)
    setWorkoutInfoData({
      ...workoutInfoData,
      status: isPublished ? 'published' : 'draft'
    })
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleDialogOpenChange} className="default-dialog">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
        <Dialog.Content minWidth="960px" padding='50px 64px'>
          <Dialog.Header p='0'>
            <Dialog.Title as="div" style={{ lineHeight: '36px' }}>
              {mode === 'add' ? 'Add New Workout Info' : 'Edit Workout Info'}
            </Dialog.Title>
            <Flex gap={3} ml="auto">
              <Box
                display="flex"
                alignItems="center"
                gap={2}
                marginRight='30px'
              >
                <Box
                  position="relative"
                  display="inline-flex"
                  alignItems="center"
                  cursor="pointer"
                  onClick={() => handlePublished(!isPublished)}
                >
                  {/* Switch Track */}
                  <Box
                    w="20px"
                    h="10px"
                    bg={isPublished ? "#5FCBA3" : "#FC3C5E"}
                    borderRadius="5px"
                    position="relative"
                    transition="background-color 0.2s"
                  />
                  {/* Switch Thumb */}
                  <Box
                    position="absolute"
                    left={isPublished ? "12px" : "2px"}
                    top="2px"
                    w="6px"
                    h="6px"
                    bg="white"
                    borderRadius="50%"
                    transition="left 0.2s"
                  />
                </Box>
                <Text color='gray.500' fontSize="sm">
                  {isPublished ? 'Published' : 'Unpublished'}
                </Text>
              </Box>
              <CustomButton onClick={() => onOpenChange(false)} visual="subtle" size="sm">Cancel</CustomButton>
              <CustomButton size="sm" onClick={handleSaveWorkoutInfo} disabled={isUploading}>
                {isUploading ? 'Saving...' : (mode === 'add' ? 'Add Workout Info' : 'Save Changes')}
              </CustomButton>
            </Flex>
          </Dialog.Header>

          <Dialog.Body p='0' mt='16px'>
            <Box>
              <Text fontWeight="medium" mb='8px'>Generic name</Text>
              <Input placeholder="Generic name" mb='24px'
                value={workoutInfoData.name}
                onChange={e => updateWorkoutInfoField('name', e.target.value)}
              />
              <Flex mb='24px'>
                <Text fontWeight="medium" mb={1} mr='32px'>Poster:</Text>
                <Flex flexDirection="column" flex={1}>
                  <RadioGroup.Root
                    colorPalette="pink"
                    variant="outline"
                    value={posterType}
                    onValueChange={handlePosterTypeChange}
                    mb='26px'
                    display="flex"
                    gap='48px'
                  >
                    <RadioGroup.Item value="upload">
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <span style={{ marginLeft: '8px' }}>Upload file</span>
                    </RadioGroup.Item>
                    <RadioGroup.Item value="url">
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <span style={{ marginLeft: '8px' }}>Image URL</span>
                    </RadioGroup.Item>
                  </RadioGroup.Root>
                  {posterType === 'upload' ? (
                    <Flex align="center" gap={3} mb={2}>
                      {posterFile ? (
                        <CustomButton visual="ghost" border="1px solid #D0CFCF" borderRadius="6px" h='48px' onClick={() => {
                          setPosterFile(null);
                          updateNestedField('poster', 'source', '');
                        }}>
                          <Box display="flex" flexDirection="column" alignItems="center">
                            <MdCancel />
                          </Box>
                        </CustomButton>
                      ) : (
                        <CustomButton as="label" visual="ghost" border="1px solid #D0CFCF" borderRadius="6px" h='48px'>
                          <Box display="flex" flexDirection="column" alignItems="center" fontWeight="medium" color="#8B8B8B" lineHeight="14px">
                            <MdFileUpload />
                            Upload
                          </Box>
                          <Input
                            type="file"
                            display="none"
                            accept="image/*"
                            onChange={e => {
                              const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                              setPosterFile(file);
                              updateNestedField('poster', 'source', file ? file.name : '');
                            }}
                          />
                        </CustomButton>
                      )}
                      <Box color="gray.500" fontSize="sm" display="flex" alignItems="center" gap={1}>
                        <MdAttachFile />
                        {posterFile ? posterFile.name : 'No file selected yet'}
                      </Box>
                    </Flex>
                  ) : (
                    <Input
                      placeholder="Image URL"
                      mb={2}
                      value={workoutInfoData?.poster?.source}
                      onChange={e => updateNestedField('poster', 'source', e.target.value)}
                    />
                  )}
                </Flex>
              </Flex>
              
              {/* Selectors Section */}
              <Text fontWeight="bold" fontSize="lg" mb={4}>Selectors</Text>
              
              <Box gap={5} display='flex' padding='16px' border='1px solid #DADADA' borderRadius='5px' marginBottom='24px'>
                <Flex flexDirection='column' style={{ width: '100%' }} gap={2}>
                  <Menu.Root>
                    <Text mb={1} fontWeight="medium">Sport</Text>
                    <Menu.Trigger>
                      <Box
                        borderWidth="1px"
                        borderRadius="md"
                        p={2}
                        minH="48px"
                        display="flex"
                        flexWrap="wrap"
                        gap={1}
                        alignItems="center"
                        bg="white"
                        position="relative"
                        cursor="pointer"
                        _hover={{ bg: 'gray.50' }}
                      >
                        {/* Selected values as tags */}
                        {workoutInfoData.selector?.sport?.map((formattedValue: any, idx: any) => (
                          <Flex
                            key={idx}
                            align="center"
                            bg="gray.100"
                            borderRadius="full"
                            px={2}
                            py={1}
                            gap={1}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Text fontSize="sm" color="gray.700">{formatConstraintValue(formattedValue)}</Text>
                            <Box
                              as="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveSelectValue('sport', formattedValue)
                              }}
                              cursor="pointer"
                              color="gray.500"
                              _hover={{ color: 'gray.700' }}
                              fontSize="12px"
                              fontWeight="bold"
                            >
                              ×
                            </Box>
                          </Flex>
                        ))}
                        
                        {/* Placeholder when no values selected */}
                        {workoutInfoData.selector?.focus?.length === 0 && (
                          <Text color="gray.400" fontSize="sm">
                            Select Sport
                          </Text>
                        )}
                        
                        {/* Dropdown for remaining values */}
                        {selectsOptions[1]?.options.length > 0 && (
                          <Box
                            position="absolute"
                            right={2}
                            top="50%"
                            transform="translateY(-50%)"
                            display="flex"
                            alignItems="center"
                            color="gray.400"
                            pointerEvents="none"
                          >
                            <MdExpandMore />
                          </Box>
                        )}
                      </Box>
                    </Menu.Trigger>
                    <Menu.Positioner>
                      <Menu.Content>
                        {selectsOptions[1]?.options.filter((opt: string) => !workoutInfoData.selector?.sport?.includes(convertFormattedValueToOriginal(opt))).map((value: string) => (
                          <Menu.Item 
                            key={value} 
                            onClick={() => handleAddSelectValue('sport', value)}
                          >
                            {value}
                          </Menu.Item>
                        ))}
                      </Menu.Content>
                    </Menu.Positioner>
                  </Menu.Root>
                  <Box>
                    <Text fontWeight="medium" mb='8px' mt='14px'>Minimum distance</Text>
                    <Input placeholder="Minimum distance in meters" mb='14px'
                      value={workoutInfoData.selector.distanceMin || ''}
                      onChange={e => handleChangeSelectorInputs('distanceMin', e.target.value)}
                      />
                  </Box>
                  <Box>
                    <Text fontWeight="medium" mb='8px'>Minimum D+</Text>
                    <Input placeholder="Minimum D+ in meters" mb='14px'
                      value={workoutInfoData.selector.dplusMin || ''}
                      onChange={e => handleChangeSelectorInputs('dplusMin', e.target.value)}
                      />
                  </Box>
                  <Menu.Root>
                    <Text mb={1} fontWeight="medium">Flags</Text>
                    <Menu.Trigger>
                      <Box
                        borderWidth="1px"
                        borderRadius="md"
                        p={2}
                        minH="48px"
                        display="flex"
                        flexWrap="wrap"
                        gap={1}
                        alignItems="center"
                        bg="white"
                        position="relative"
                        cursor="pointer"
                        _hover={{ bg: 'gray.50' }}
                      >
                        {/* Selected values as tags */}
                        {workoutInfoData.selector?.flags?.map((formattedValue: any, idx: any) => (
                          <Flex
                            key={idx}
                            align="center"
                            bg="gray.100"
                            borderRadius="full"
                            px={2}
                            py={1}
                            gap={1}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Text fontSize="sm" color="gray.700">{formatConstraintValue(formattedValue)}</Text>
                            <Box
                              as="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveSelectValue('flags', formattedValue)
                              }}
                              cursor="pointer"
                              color="gray.500"
                              _hover={{ color: 'gray.700' }}
                              fontSize="12px"
                              fontWeight="bold"
                            >
                              ×
                            </Box>
                          </Flex>
                        ))}
                        
                        {/* Placeholder when no values selected */}
                        {workoutInfoData.selector?.flags?.length === 0 && (
                          <Text color="gray.400" fontSize="sm">
                            Select Flag
                          </Text>
                        )}
                        
                        {/* Dropdown for remaining values */}
                        {selectsOptions[2]?.options.length > 0 && (
                          <Box
                            position="absolute"
                            right={2}
                            top="50%"
                            transform="translateY(-50%)"
                            display="flex"
                            alignItems="center"
                            color="gray.400"
                            pointerEvents="none"
                          >
                            <MdExpandMore />
                          </Box>
                        )}
                      </Box>
                    </Menu.Trigger>
                    <Menu.Positioner>
                      <Menu.Content>
                        {selectsOptions[2]?.options.filter((opt: string) => !workoutInfoData.selector?.flags?.includes(convertFormattedValueToOriginal(opt))).map((value: string) => (
                          <Menu.Item 
                            key={value} 
                            onClick={() => handleAddSelectValue('flags', value)}
                          >
                            {value}
                          </Menu.Item>
                        ))}
                      </Menu.Content>
                    </Menu.Positioner>
                  </Menu.Root>
                </Flex>
                <Flex flexDirection='column' style={{ width: '100%' }} gap={2}>
                  <Flex gap={10} mt='4px'>
                    <Box>
                      <Text fontWeight="medium" mb='8px'>Minimum time</Text>
                      <Input placeholder="hh:mm:ss" mb='10px'
                        value={workoutInfoData.selector.timeMin || ''}
                        onChange={e => handleChangeSelectorInputs('timeMin', e.target.value)}
                        />
                    </Box>
                    <Box>
                      <Text fontWeight="medium" mb='8px'>Maximum time</Text>
                      <Input placeholder="hh:mm:ss" mb='10px'
                        value={workoutInfoData.selector.timeMax || ''}
                        onChange={e => handleChangeSelectorInputs('timeMax', e.target.value)}
                        />
                    </Box>
                  </Flex>
                  <Box>
                    <Text fontWeight="medium" mb='8px' mt='4px'>Maximum distance</Text>
                    <Input placeholder="Maximum distance in meters" mb='14px'
                      value={workoutInfoData.selector.distanceMax || ''}
                      onChange={e => handleChangeSelectorInputs('distanceMax', e.target.value)}
                      />
                  </Box>
                  <Box>
                    <Text fontWeight="medium" mb='8px' mt='0px'>Maximum D+</Text>
                    <Input placeholder="Maximum D+ in meters" mb='14px'
                      value={workoutInfoData.selector.dplusMax || ''}
                      onChange={e => handleChangeSelectorInputs('dplusMax', e.target.value)}
                      />
                  </Box>
                  <Menu.Root>
                  <Text mb={1} fontWeight="medium">Focus</Text>
                  <Menu.Trigger>
                    <Box
                      borderWidth="1px"
                      borderRadius="md"
                      p={2}
                      minH="48px"
                      display="flex"
                      flexWrap="wrap"
                      gap={1}
                      alignItems="center"
                      bg="white"
                      position="relative"
                      cursor="pointer"
                      _hover={{ bg: 'gray.50' }}
                    >
                      {/* Selected values as tags */}
                      {workoutInfoData.selector?.focus?.map((formattedValue: any, idx: any) => (
                        <Flex
                          key={idx}
                          align="center"
                          bg="gray.100"
                          borderRadius="full"
                          px={2}
                          py={1}
                          gap={1}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Text fontSize="sm" color="gray.700">{formatConstraintValue(formattedValue)}</Text>
                          <Box
                            as="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveSelectValue('focus', formattedValue)
                            }}
                            cursor="pointer"
                            color="gray.500"
                            _hover={{ color: 'gray.700' }}
                            fontSize="12px"
                            fontWeight="bold"
                          >
                            ×
                          </Box>
                        </Flex>
                      ))}
                      
                      {/* Placeholder when no values selected */}
                      {workoutInfoData.selector?.focus?.length === 0 && (
                        <Text color="gray.400" fontSize="sm">
                          Select Focus
                        </Text>
                      )}
                      
                      {/* Dropdown for remaining values */}
                      {selectsOptions[0]?.options.length > 0 && (
                        <Box
                          position="absolute"
                          right={2}
                          top="50%"
                          transform="translateY(-50%)"
                          display="flex"
                          alignItems="center"
                          color="gray.400"
                          pointerEvents="none"
                        >
                          <MdExpandMore />
                        </Box>
                      )}
                    </Box>
                  </Menu.Trigger>
                  <Menu.Positioner>
                    <Menu.Content>
                      {selectsOptions[0]?.options.filter((opt: string) => !workoutInfoData.selector?.focus?.includes(convertFormattedValueToOriginal(opt))).map((value: string) => (
                        <Menu.Item 
                          key={value} 
                          onClick={() => handleAddSelectValue('focus', value)}
                        >
                          {value}
                        </Menu.Item>
                      ))}
                    </Menu.Content>
                  </Menu.Positioner>
                </Menu.Root>
                </Flex>
              </Box>
              {/* Selectors Section End */}

              <Box padding='16px' border='1px solid #DADADA' borderRadius='5px'>
                <Tabs.Root value={langTab} onValueChange={handleLangTabChange} variant="subtle" colorPalette="pink" mb={2}>
                  <Tabs.List mb='24px' gap='24px'>
                    {languages.map((lang: string, idx: number) => (
                      <Tabs.Trigger key={lang} value={lang}>
                        {lang}
                      </Tabs.Trigger>
                    ))}
                  </Tabs.List>
                  {languages.map((lang: string) => (
                    <Tabs.Content key={lang} value={lang}>
                      <Box style={{ paddingLeft: 0, paddingRight: 0 }}>
                        <Text fontWeight="medium" mb='8px'>Display Name</Text>
                        <Input
                          placeholder="Display Name"
                          mb='24px'
                          value={findTextDataByLanguage('displayName', lang)?.text || ''}
                          onChange={e => updateTextDataByLanguage('displayName', lang, e.target.value)}
                        />
                        <Text fontWeight="medium" mb='8px'>Description</Text>
                        <Textarea
                          placeholder="Description"
                          mb='24px'
                          value={findTextDataByLanguage('description', lang)?.text || ''}
                          onChange={e => updateTextDataByLanguage('description', lang, e.target.value)}
                        />
                        {/* Youtube Video Upload - match background video UI */}
                        <Flex mb='24px'>
                          <Text fontWeight="medium" mb={1} mr='32px'>Youtube Video:</Text>
                          <Flex flexDirection="column" flex={1}>
                            <RadioGroup.Root colorPalette="pink" variant="outline" value={translationVideoTypes[lang] || 'url'} onValueChange={(details: ValueChangeDetails<'upload' | 'url'>) => updateTranslationVideoType(lang, details.value)} mb='26px' display="flex" gap='48px'>
                              <RadioGroup.Item value="upload">
                                <RadioGroup.ItemHiddenInput />
                                <RadioGroup.ItemIndicator />
                                <span style={{ marginLeft: '8px' }}>Upload file</span>
                              </RadioGroup.Item>
                              <RadioGroup.Item value="url">
                                <RadioGroup.ItemHiddenInput />
                                <RadioGroup.ItemIndicator />
                                <span style={{ marginLeft: '8px' }}>Youtube URL</span>
                              </RadioGroup.Item>
                            </RadioGroup.Root>
                            {translationVideoTypes[lang] === 'upload' ? (
                              <Flex align="center" gap={3} mb={2}>
                                {translationVideoFiles[lang] ? (
                                  <CustomButton visual="ghost" h='48px' border="1px solid #D0CFCF" borderRadius="6px" onClick={() => {
                                    updateTranslationVideoFile(lang, null);
                                    updateVideoDataByLanguage(lang, 'source', '');
                                  }}>
                                    <Box display="flex" flexDirection="column" alignItems="center">
                                      <MdCancel />
                                    </Box>
                                  </CustomButton>
                                ) : (
                                  <CustomButton as="label" visual="ghost" border="1px solid #D0CFCF" borderRadius="6px" h='48px'>
                                    <Box display="flex" flexDirection="column" alignItems="center" fontWeight="medium" color="#8B8B8B" lineHeight="14px">
                                      <MdFileUpload />
                                      Upload
                                    </Box>
                                    <Input
                                      type="file"
                                      display="none"
                                      accept="video/*"
                                      onChange={e => {
                                        const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                                        updateTranslationVideoFile(lang, file);
                                        updateVideoDataByLanguage(lang, 'source', file ? file.name : '');
                                      }}
                                    />
                                  </CustomButton>
                                )}
                                <Box color="gray.500" fontSize="sm" display="flex" alignItems="center" gap={1}>
                                  <MdAttachFile />
                                  {translationVideoFiles[lang] ? translationVideoFiles[lang]?.name : 'No file selected yet'}
                                </Box>
                              </Flex>
                            ) : (
                              <Input
                                placeholder="Youtube URL"
                                mb={2}
                                value={findVideoDataByLanguage(lang)?.video?.source || ''}
                                onChange={e => updateVideoDataByLanguage(lang, 'source', e.target.value)}
                              />
                            )}
                          </Flex>
                        </Flex>
                        {/* End Youtube Video Upload */}
                        <Flex gap='24px' align="center">
                          <Box flex={1}>
                            <Text fontWeight="medium" mb='8px'>Youtube Video Title</Text>
                            <Input
                              placeholder="What you want the user to see in the app"
                              flex={1}
                              value={findVideoDataByLanguage(lang)?.video?.title || ''}
                              onChange={e => {
                                updateVideoDataByLanguage(lang, 'title', e.target.value);
                              }}
                            />
                          </Box>
                          <Box width="120px">
                            <Text fontWeight="medium" mb='8px'>Duration</Text>
                            <Input
                              placeholder="hh:mm:ss"
                              width="120px"
                              value={findVideoDataByLanguage(lang)?.video?.duration || ''}
                              onChange={e => {
                                updateVideoDataByLanguage(lang, 'duration', e.target.value);
                              }}
                            />
                          </Box>
                        </Flex>
                      </Box>
                    </Tabs.Content>
                  ))}
                </Tabs.Root>
              </Box>
            </Box>
          </Dialog.Body>
        </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}; 